'use client';

import { HsaFsaInlineLogo } from '@jouzen/ecom-components/icons/HsaFsaInlineLogo';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';

import RichText from '../RichText';

const LOCATION = 'eyebrow banner';

//the hidden afilliate eyebrow by default
export const AffiliateMessage = () => {
  // Fire custom event when component mounts to allow third-parties like CP to inject content
  useEffect(() => {
    const event = new CustomEvent('partnereyebrowmounted');
    window.dispatchEvent(event);
  }, []);

  return <div id="partner_eyebrow_text" data-cy="partner-eyebrow-message" />;
};
interface OnLinkClickProps {
  readonly additionalSegmentProperties?: Record<string, unknown> | undefined;
  readonly page: string;
}

const onLinkClick = async ({
  additionalSegmentProperties,
  page,
}: OnLinkClickProps): Promise<void> => {
  await window.ouraAnalytics.track(EventType.LinkClicked, {
    location: LOCATION,
    page,
    ...additionalSegmentProperties,
  });
};

export interface GenericEyebrowProps {
  readonly localeKey: MessageKey;
  readonly url?: string;
  readonly target?: string;
  readonly additionalSegmentProperties?: Record<string, unknown> | undefined;
  readonly className?: string;
}

interface LinkProps extends Omit<GenericEyebrowProps, 'localeKey'> {
  readonly chunks: ReactNode;
  readonly page: string;
}

const link = ({
  additionalSegmentProperties,
  chunks,
  page,
  target,
  url,
}: LinkProps) => {
  return (
    <Link
      href={url!}
      target={target}
      data-cy="eyebrow-cta-link"
      className="text-nowrap underline"
      onClick={() => onLinkClick({ additionalSegmentProperties, page })}
    >
      {chunks}
    </Link>
  );
};

const icon = () => (
  <span
    aria-hidden="true"
    className="block align-middle"
    data-cy="hsa-fsa-inline-logo"
  >
    <HsaFsaInlineLogo />
  </span>
);
/**
 * GenericEyebrow component driven by config in Statsig
 * */
export const GenericEyebrow = ({
  localeKey,
  url = '',
  target = '_self',
  additionalSegmentProperties,
  className = '',
}: GenericEyebrowProps) => {
  const page = usePathname();

  return (
    <Typography
      variant="body-small"
      weight="bold"
      color="inherit"
      data-cy="generic-eyebrow-message"
      className={cx(
        'flex-wrap items-center justify-center gap-x-1 text-center',
        {
          'inline-flex': !className.includes('block'),
        },
        className,
      )}
      style={{ hyphens: 'none' }}
    >
      <RichText
        messageKey={localeKey}
        tValues={{
          link: (chunks) =>
            link({
              additionalSegmentProperties,
              chunks,
              page,
              target,
              url,
            }),
          icon,
        }}
      />
    </Typography>
  );
};
